import { useState, useCallback } from "react";
import { useSidebar } from "../context/SidebarContext";
import { Layers } from "lucide-react";

/**
 * Scalekit (d2c dashboard) opener for the merchant sidebar.
 * 
 * Demo mode: opens D2C dashboard in new tab
 */

// Scalekit wordmark assets (public/), light/dark aware like the iSight button.
// Black logo on light sidebar, white logo on dark sidebar.
const SCALEKIT_LOGO_LIGHT = "/Scalekit-black%201.svg";
const SCALEKIT_LOGO_DARK = "/Scalekit-white%201.svg";

interface ScalekitOpenButtonProps {
  variant?: "sidebar" | "header";
  /** When true the button is visible but non-interactive (e.g. billing). */
  navigationDisabled?: boolean;
}

export function ScalekitOpenButton({
  variant = "sidebar",
  navigationDisabled = false,
}: ScalekitOpenButtonProps) {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();
  const [loading, setLoading] = useState(false);

  const expanded = isExpanded || isHovered || isMobileOpen;

  const handleOpenScalekit = useCallback(() => {
    if (navigationDisabled) return;
    
    // Set demo auth token (the auth system checks for 'token' in localStorage)
    localStorage.setItem('token', 'demo-session-token');
    
    // Set demo user data
    localStorage.setItem('scalekit_demo_user', JSON.stringify({
      name: 'Demo User',
      email: 'demo@scalekit.com'
    }));
    
    // Open D2C dashboard in new tab
    window.open('/scalekit/dashboard', '_blank', 'noopener,noreferrer');
  }, [navigationDisabled]);

  return (
    <div className={variant === "sidebar" ? "shrink-0" : ""}>
      <button
        type="button"
        onClick={handleOpenScalekit}
        disabled={navigationDisabled || loading}
        title={!expanded ? "Scalekit" : "Open Scalekit"}
        className={`menu-item group w-full flex items-center ${
          loading ? "menu-item-active" : "menu-item-inactive"
        } ${
          !expanded ? "lg:justify-start" : "justify-start px-3"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {expanded ? (
          <>
            <span className="flex min-w-0 items-center">
              {loading ? (
                <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[#40bfb9] border-t-transparent" />
              ) : (
                <>
                  <img
                    src={SCALEKIT_LOGO_LIGHT}
                    alt="Scalekit"
                    className="object-contain dark:hidden"
                    style={{ height: 22, width: "auto", maxWidth: "100%" }}
                  />
                  <img
                    src={SCALEKIT_LOGO_DARK}
                    alt="Scalekit"
                    className="hidden object-contain dark:block"
                    style={{ height: 22, width: "auto", maxWidth: "100%" }}
                  />
                </>
              )}
            </span>
            <span className="flex shrink-0 items-center">
              <sup className="shrink-0 rounded-full bg-[#2dd4bf]/15 px-2 py-2 text-[10px] font-bold uppercase tracking-wide text-[#3e9e58] dark:text-[#2dd4bf]">
                Free
              </sup>
            </span>
          </>
        ) : (
          <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
            {loading ? (
              <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[#40bfb9] border-t-transparent" />
            ) : (
              <Layers className="h-5 w-5 text-[#A3A3A3] group-hover:text-[#40bfb9]" />
            )}
          </span>
        )}
      </button>
    </div>
  );
}

export default ScalekitOpenButton;
